import { useState, useEffect, useContext } from "react";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { getAllChartOfAccounts } from "../../../services/accounting";
import { useQueries, useQueryClient } from "react-query";
import {
  calculateTaxInclusiveAmount,
  calculateTaxAmount,
} from "../../../helpers";
import {
  useCreateJournalEntryMutation,
  useDraftJournalEntryMutation,
} from "../../../hooks/api/mutations/useAccounting";
import { RequestLoader, ToastContext } from "../../../hooks/context";
import { cleanObject, getRandomIntInclusive } from "../../../helpers";
import { useNavigate } from "react-router-dom";
import { pathConstants } from "../../../routes/pathContants";
import useGetPermission from "../../../hooks/useGetPermission";
import { useGetSuppliers } from "../../../hooks/api/queries";

const useJournalEntryForm = (
  isEditMode = false,
  draftJournalEntryDetails = null,
) => {
  const { storeList } = useSelector((state) => state.profileDetailsReducer);
  const [useNarrationToLineDescription, setUseNarrationToLineDescription] =
    useState(false);
  const { setRequestLoaderProgress } = useContext(RequestLoader);
  const triggerToast = useContext(ToastContext);
  const createJournalEntryMutation = useCreateJournalEntryMutation();
  const draftJournalEntryMutation = useDraftJournalEntryMutation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { isPermitted: canPostOrApproveJournal } = useGetPermission({
    permissionParentKey: "Accounting",
    permissionChildKey: "can_post_or_approve_journal_entry",
  });
  const taxOptions = {
    no_tax: "no_vat",
    tax_inclusive: "vat_inclusive",
    tax_exclusive: "vat_exclusive",
  };
  const taxOptionLabels = [
    { label: "No Tax", value: taxOptions.no_tax },
    { label: "Tax Inclusive", value: taxOptions.tax_inclusive },
    { label: "Tax Exclusive", value: taxOptions.tax_exclusive },
  ];

  const getInitialLine = () => ({
    id: uuidv4(),
    chart_account_id: "",
    description: "",
    debit_amount: "",
    credit_amount: "",
    tax_rate: 0,
  });

  const extractLineItemsFromDraftDetails = (draftDetails) => {
    if (!draftDetails?.line_items?.length) {
      return [getInitialLine(), getInitialLine()];
    }

    return draftDetails.line_items.map((line) => ({
      id: uuidv4(),
      chart_account_id: line?.chart_account?.id || "",
      description: line.description,
      debit_amount: line.total_debit_amount || "",
      credit_amount: line.total_credit_amount || "",
      tax_rate: line.tax_rate,
    }));
  };

  const [journalEntryDetails, setJournalEntryDetails] = useState({
    reference: isEditMode ? draftJournalEntryDetails?.reference : "",
    narration: isEditMode ? draftJournalEntryDetails?.description : "",
    entry_date: isEditMode ? draftJournalEntryDetails?.entry_date : "",
    supplier_id: isEditMode ? draftJournalEntryDetails?.supplier?.id : "",
    supplier_name: isEditMode
      ? draftJournalEntryDetails?.supplier?.name || ""
      : "",
    tax_type: isEditMode
      ? draftJournalEntryDetails?.tax_type
      : taxOptions.no_tax,
    store_id: isEditMode ? draftJournalEntryDetails?.store?.id : "",
    customer_id: isEditMode ? draftJournalEntryDetails?.customer?.id : "",
    customer_name: isEditMode
      ? draftJournalEntryDetails?.customer?.full_name
      : "",
    lines: isEditMode
      ? extractLineItemsFromDraftDetails(draftJournalEntryDetails)
      : [getInitialLine(), getInitialLine()],
  });

  const getNewLineData = () => ({
    id: uuidv4(),
    chart_account_id: "",
    description: useNarrationToLineDescription
      ? journalEntryDetails?.narration
      : "",
    debit_amount: "",
    credit_amount: "",
    tax_rate: 0,
  });

  const addNewLine = () => {
    const newLine = getNewLineData();

    setJournalEntryDetails((prevState) => ({
      ...prevState,
      lines: [...prevState.lines, newLine],
    }));
  };

  const [summary, setSummary] = useState({
    total_debit_tax: 0,
    total_credit_tax: 0,
    subtotal_debit: 0,
    subtotal_credit: 0,
    total_debit: 0,
    total_credit: 0,
    total_credit_with_tax: 0,
    total_debit_with_tax: 0,
    accountNotBalanced: false,
  });

  const [allChartOfAccounts] = useQueries([
    {
      queryKey: ["getAllChartOfAccounts"],
      staleTime: Infinity,
      queryFn: () => getAllChartOfAccounts().then((res) => res?.data?.data),
    },
  ]);
  const { data: suppliers, isLoading: isLoadingSupplier } = useGetSuppliers();
  const reCalculateSummary = () => {
    let isTaxInclusive =
      journalEntryDetails.tax_type === taxOptions.tax_inclusive;

    let total_debit_tax = journalEntryDetails.lines
      .map((line) =>
        Number(
          isTaxInclusive
            ? calculateTaxInclusiveAmount({
                taxRate: line.tax_rate,
                principalAmount: line.debit_amount,
              })
            : calculateTaxAmount({
                taxRate: line.tax_rate,
                principalAmount: line.debit_amount,
              }),
        ),
      )
      .reduce((acc, curr) => acc + curr, 0);

    let total_credit_tax = journalEntryDetails.lines
      .map((line) =>
        Number(
          isTaxInclusive
            ? calculateTaxInclusiveAmount({
                taxRate: line.tax_rate,
                principalAmount: line.credit_amount,
              })
            : calculateTaxAmount({
                taxRate: line.tax_rate,
                principalAmount: line.credit_amount,
              }),
        ),
      )
      .reduce((acc, curr) => acc + curr, 0);

    let subtotal_debit = journalEntryDetails.lines
      .map((line) => Number(line.debit_amount) || 0)
      .reduce((acc, curr) => acc + curr, 0);

    let subtotal_credit = journalEntryDetails.lines
      .map((line) => Number(line.credit_amount) || 0)
      .reduce((acc, curr) => acc + curr, 0);

    if (isTaxInclusive) {
      subtotal_debit = subtotal_debit - total_debit_tax;
      subtotal_credit = subtotal_credit - total_credit_tax;
    }

    let total_credit = subtotal_credit + total_credit_tax;
    let total_debit = subtotal_debit + total_debit_tax;

    let accountNotBalanced =
      Number(total_credit.toFixed(2)) !== Number(total_debit.toFixed(2));

    setSummary({
      total_debit_tax,
      total_credit_tax,
      subtotal_debit,
      subtotal_credit,
      total_debit,
      total_credit,
      accountNotBalanced,
    });
  };

  useEffect(() => {
    reCalculateSummary();
  }, [journalEntryDetails.lines, journalEntryDetails.tax_type]);

  const handleJournalEntrySubmit = () => {
    setRequestLoaderProgress(getRandomIntInclusive(10, 50));
    const data = { ...journalEntryDetails };
    createJournalEntryMutation.mutate(cleanObject(data), {
      onSuccess: (res) => {
        triggerToast(res?.data?.detail, "success");
        queryClient.invalidateQueries(["getJournalEntries"]);
        if (canPostOrApproveJournal) {
          navigate(pathConstants.ACCOUNTING_JOURNAL_ENTRIES);
        } else {
          navigate(
            pathConstants.ACCOUNTING_JOURNAL_ENTRIES + "?tab=review_tab",
          );
        }
      },

      onSettled: () => setRequestLoaderProgress(100),
    });
  };

  const handleDraftJournalEntrySubmit = () => {
    setRequestLoaderProgress(getRandomIntInclusive(10, 50));
    const data = { ...journalEntryDetails };
    draftJournalEntryMutation.mutate(cleanObject(data), {
      onSuccess: (res) => {
        triggerToast(res?.data?.detail, "success");
        queryClient.invalidateQueries(["getJournalEntryDetails"]);
        navigate(pathConstants.ACCOUNTING_JOURNAL_ENTRIES + "?tab=draft_tab");
      },

      onSettled: () => setRequestLoaderProgress(100),
    });
  };

  return {
    storeList,
    journalEntryDetails,
    setJournalEntryDetails,
    useNarrationToLineDescription,
    setUseNarrationToLineDescription,
    addNewLine,
    taxOptions,
    taxOptionLabels,
    allChartOfAccounts,
    summary,
    handleJournalEntrySubmit,
    handleDraftJournalEntrySubmit,
    createJournalEntryMutation,
    draftJournalEntryMutation,
    suppliers,
    isLoadingSupplier,
  };
};

export default useJournalEntryForm;
